'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Loader2 } from 'lucide-react';
import { apiGetAllNews } from '@/services/newsHome/api';

interface ServiceItem {
    id: string;
    title: string;
    description: string;
    content: string;
    imageUrl: string;
    url?: string;
}


interface DetailsInvestmentProps {
    companyId?: string | number;
}


const FALLBACK_HEADERS: Record<string, string> = {
    "investment": `
        <p class="text-[10px] md:text-xs font-medium text-[#006633] uppercase tracking-wider mb-2">
            CHÚNG TÔI CUNG CẤP GIẢI PHÁP NGUỒN VỐN CHO DOANH NGHIỆP Ở
        </p>
        <h2 class="text-lg md:text-xl lg:text-2xl font-medium text-[#333333] leading-snug max-w-xl mx-auto">
            Bất kể mọi giai đoạn phát triển –
            <span class="text-[#006633] ml-1">Không giới hạn quy mô kinh doanh</span>
        </h2>
    `,
    "travel": `
        <p class="text-[10px] md:text-xs font-medium text-[#006633] uppercase tracking-wider mb-2">
           CÁC DỊCH VỤ CỦA CHÚNG TÔI
        </p>
        <h2 class="text-lg md:text-xl lg:text-2xl font-medium text-[#333333] leading-snug max-w-xl mx-auto">
            Dịch vụ du lịch tại <span class="text-[#006633] ml-1">Rovi Travel</span>
        </h2>
    `,
    "hospitality": `
        <p class="text-[10px] md:text-xs font-medium text-[#006633] uppercase tracking-wider mb-2">
             CÁC DỊCH VỤ CỦA CHÚNG TÔI
        </p>
        <h2 class="text-lg md:text-xl lg:text-2xl font-medium text-[#333333] leading-snug max-w-xl mx-auto">
            Dịch vụ tại <span class="text-[#006633] ml-1">Rovi Hospitality</span>
        </h2>
    `,
    "logistics": `
        <p class="text-[10px] md:text-xs font-medium text-[#006633] uppercase tracking-wider mb-2">
             CÁC DỊCH VỤ CỦA CHÚNG TÔI
        </p>
        <h2 class="text-lg md:text-xl lg:text-2xl font-medium text-[#333333] leading-snug max-w-xl mx-auto">
            Dịch vụ tại <span class="text-[#006633] ml-1">Rovi Logistics</span>
        </h2>
    `
};


export default function DetailsInvestment({ companyId }: DetailsInvestmentProps) {
    const params = useParams();

    const finalId = companyId ? String(companyId) : (params.id as string);

    const { data: newsData, isLoading } = useQuery({
        queryKey: ['services', finalId],
        queryFn: async () => {
            const res = await apiGetAllNews({
                keywords: '',
                newsTypeId: Number(finalId),
                isPublic: '',
                page: 1,
                pageSize: 50,
            });
            return res?.isOK ? res.result.items : [];
        },
        enabled: !!finalId && !isNaN(Number(finalId)),
        staleTime: 5 * 60 * 1000,
    });

    // --- 1. Hàm tạo Slug chuẩn ---
    const createSlug = (title: string) => {
        return title.toLowerCase()
            .trim()
            .replace(/[áàảãạăắằẳẵặâấầẩẫậ]/g, 'a')
            .replace(/[éèẻẽẹêếềểễệ]/g, 'e')
            .replace(/[iíìỉĩị]/g, 'i')
            .replace(/[óòỏõọôốồổỗộơớờởỡợ]/g, 'o')
            .replace(/[úùủũụưứừửữự]/g, 'u')
            .replace(/[ýỳỷỹỵ]/g, 'y')
            .replace(/đ/g, 'd')
            .replace(/[^a-z0-9-]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
    };

    let headerContent = "";

    if (newsData && Array.isArray(newsData) && newsData.length > 0) {
        const headerItem = newsData.find((item: any) => item.seoDescription === 'Tiêu đề dịch vụ');

        if (headerItem) {
            headerContent = headerItem.content || "";
        } else {
            const categoryName = newsData[0].newsType?.title || "";
            const foundKey = Object.keys(FALLBACK_HEADERS).find(key =>
                categoryName.toLowerCase().includes(key.toLowerCase())
            );
            headerContent = foundKey ? FALLBACK_HEADERS[foundKey] : FALLBACK_HEADERS["investment"];
        }
    }

    const servicesData: ServiceItem[] = isLoading || !newsData?.length
        ? []
        : newsData
            .filter((item: any) => item.seoDescription === 'Dịch vụ')
            .map((item: any) => ({
                id: item.id.toString(),
                title: item.title || "Dịch vụ Rovi",
                description: item.description
                    ? item.description.replace(/(?:\r\n|\r|\n)/g, '<br />')
                    : "",
                // --------------------------------------------------
                imageUrl: item.image
                    ? item.image.replace(/^<|>$/g, '').trim()
                    : '/images/placeholder.jpg',
                content: item.content || '',
                url: item.url,
            }));

    if (isLoading) {
        return (
            <section className="py-16 px-4 flex justify-center items-center bg-white">
                <div className="flex items-center gap-3 text-gray-600">
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <span className="text-sm font-medium">Đang tải dữ liệu dịch vụ...</span>
                </div>
            </section>
        );
    }

    if (!isLoading && servicesData.length === 0) {
        return null;
    }

    return (
        <section className="py-5 px-4 md:px-6">
            <div className="max-w-[60rem] mx-auto">
                <div className="bg-[#EFEFEF] lg:bg-white rounded-2xl p-8 md:p-8 shadow-md border border-gray-200 lg:p-10 overflow-hidden">

                    <div className="titleEco prose text-center mb-8" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                        <div dangerouslySetInnerHTML={{ __html: headerContent }} />
                    </div>

                    <div className="h-px bg-gray-100 my-8" />

                    <div className="space-y-0 lg:space-y-20" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                        {servicesData.map((service, index) => {

                            // --- 2. Tạo URL: ten-dich-vu-{id} ---
                            const slugTitle = createSlug(service.title);
                            const internalLink = `/news/${slugTitle}-${service.id}`;
                            const buttonLink = service.url && service.url.trim() !== ""
                                ? service.url
                                : internalLink;

                            return (
                                <div key={service.id}>
                                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-stretch">

                                        <div className={`lg:col-span-5 flex flex-col ${index % 2 === 1 ? 'lg:order-2' : 'lg:order-1'}`}>
                                            <div>
                                                <h3 className="text-lg font-bold text-[#333333] mb-2">
                                                    <Link
                                                        href={internalLink}
                                                        className="hover:text-[#006633] transition-colors duration-300"
                                                    >
                                                        {service.title}
                                                    </Link>
                                                </h3>

                                                {/* Lưu ý: CSS line-clamp có thể cắt bớt nội dung nếu quá dài.
                                                    Nếu muốn hiển thị hết khi có nhiều thẻ <br>, bạn có thể cân nhắc bỏ line-clamp
                                                */}
                                                <div
                                                    className="text-xs text-[#333333] mb-4 text-justify leading-relaxed line-clamp-2 md:line-clamp-4 lg:line-clamp-[8] overflow-hidden text-ellipsis"
                                                    dangerouslySetInnerHTML={{ __html: service.description }}
                                                />

                                            </div>

                                            <div className="flex gap-2 pt-2 text-[#FFFFFF]">
                                                <Link
                                                    href="/contact"
                                                    className="inline-flex items-center gap-1 px-4 py-2 bg-[#006633] text-[#FFFFFF] rounded-full font-semibold transition-all hover:bg-red-600 active:scale-95 text-[10px] tracking-wide"
                                                    aria-label="Liên hệ ngay"
                                                >
                                                    Liên hệ ngay
                                                    <ChevronRight size={12} />
                                                </Link>

                                                <Link
                                                    href={buttonLink}
                                                    target={service.url ? "_blank" : "_self"} // Nếu là URL ngoài thì mở tab mới
                                                    rel={service.url ? "noopener noreferrer" : ""}
                                                    className="inline-flex items-center gap-1 px-4 py-2 border border-[#006633] text-[#006633] rounded-full font-semibold transition-all hover:bg-[#006633] hover:border-[#006633] hover:text-white active:scale-95 bg-white text-[10px] tracking-wide"
                                                    aria-label="Tìm hiểu thêm"
                                                >
                                                    Tìm hiểu thêm
                                                    <ChevronRight size={12} />
                                                </Link>
                                            </div>
                                        </div>

                                        <div className={`lg:col-span-7 relative ${index % 2 === 1 ? 'lg:order-1' : 'lg:order-2'}`}>
                                            <Link
                                                href={internalLink}
                                                className="block relative w-full aspect-[16/9] rounded-xl overflow-hidden bg-gray-100 group cursor-pointer"
                                            >
                                                {service.imageUrl ? (
                                                    <Image
                                                        src={service.imageUrl}
                                                        alt={service.title}
                                                        fill
                                                        className="object-cover transition-transform duration-500 group-hover:scale-105"
                                                        sizes="(max-width: 768px) 100vw, 50vw"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 text-gray-400">
                                                        <span className="text-[10px] font-medium">No Image</span>
                                                    </div>
                                                )}
                                            </Link>
                                        </div>

                                    </div>

                                    {index < servicesData.length - 1 && (
                                        <div className="h-px bg-gray-100 my-8 lg:my-10" />
                                    )}
                                </div>
                            );
                        })}
                    </div>

                </div>
            </div>
        </section>
    );
}